import { AsyncPipe, NgClass } from '@angular/common';
import { AfterViewInit, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { select, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map, tap } from 'rxjs';

import { ButtonComponent } from '@evc/web-components';
import { TranslatePipe } from '@shared/services/i18n/i18n.module';

import { clearAlert, clearFile, resetUpload } from '@app/store/actions/upload/upload.action';
import type { CurrentUploadState } from '@app/store/reducers';
import {
  getUploadAlerts,
  getUploadFiles,
  hasUploadError,
  isUploadComplete,
} from '@app/store/selectors';
import { UploadFileComponent } from './upload-file/upload-file.component';

@Component({
  selector: 'drive-upload-list',
  templateUrl: 'upload-list.component.html',
  styleUrls: ['upload-list.component.scss'],
  imports: [
    ButtonComponent,
    UploadFileComponent,
    TranslatePipe,
    AsyncPipe,
    NgClass,
  ],
})
export class UploadListComponent implements AfterViewInit {
  readonly #store = inject(Store);
  readonly #bottomSheetRef: MatBottomSheetRef<UploadListComponent> = inject(MatBottomSheetRef);

  files$: Observable<CurrentUploadState[]> = this.#store.pipe(select(getUploadFiles));
  hasNoAlert$: Observable<boolean> = this.#store.pipe(
    select(getUploadAlerts),
    map((alerts: number) => !!alerts),
    tap((hasAlert) => {
      if (hasAlert && !this.minified()) {
        this.#store.dispatch(clearAlert());
      }
    }),
    map((hasAlert: boolean) => !hasAlert),
  );

  isUploadComplete = toSignal<boolean>(this.#store.pipe(select(isUploadComplete)));
  isUploading = computed(() => !this.isUploadComplete());
  hasError = toSignal<boolean>(this.#store.pipe(select(hasUploadError)));
  minified = signal(false);

  ngAfterViewInit(): void {
    this.#bottomSheetRef.afterDismissed().subscribe(() => {
      this.#store.dispatch(resetUpload());
    });
  }

  close(auto = false): void {
    if (auto && this.hasError()) return;

    this.#bottomSheetRef.dismiss();
  }

  toggle(): void {
    this.minified.update((minified) => {
      if (minified) this.#store.dispatch(clearAlert());

      return !minified;
    });
  }

  cancelFile(state: CurrentUploadState): void {
    this.#store.dispatch(
      clearFile({
        name: state.file?.name,
        fileId: state.uploadId,
      }),
    );
  }

  trackByName(index: number, item: { file: { name: string } }): string {
    return `${item.file.name}-${index}`;
  }
}
